/** Appointment alerts: which times apply, and how they read. */

export interface AlertSettings {
  "calendar.alertsTimed": number[];
  "calendar.alertsAllDay": number[];
  "calendar.alertsByType": Record<string, number[] | "google">;
}

/**
 * An appointment's alert times, minutes before it starts (all-day: before midnight that day). Its
 * own, else its type's, else the company's. google: the Google event uses each person's own defaults
 * (the CRM's reminders still go at the company's times).
 */
export function alertsFor(a: { type: string; allDay: boolean; alerts: number[] | null }, s: AlertSettings): { minutes: number[]; google: boolean } {
  const company = a.allDay ? s["calendar.alertsAllDay"] : s["calendar.alertsTimed"];
  if (a.alerts) return { minutes: a.alerts, google: false };
  const byType = s["calendar.alertsByType"][a.type];
  if (byType === "google") return { minutes: company, google: true };
  return { minutes: byType ?? company, google: false };
}

/** "1 day before", "1 hour before", "6 PM the day before" (all-day). */
export function alertLabel(minutes: number, allDay: boolean): string {
  if (allDay) {
    if (minutes === 0) return "Midnight that day";
    const days = Math.ceil(minutes / 1440);
    const at = days * 1440 - minutes;
    const h = Math.floor(at / 60);
    const m = at % 60;
    const time = `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`;
    return `${time} ${days === 1 ? "the day before" : `${days} days before`}`;
  }
  if (minutes === 0) return "At the start";
  if (minutes % 10080 === 0) return `${minutes / 10080} week${minutes === 10080 ? "" : "s"} before`;
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes === 1440 ? "" : "s"} before`;
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes === 60 ? "" : "s"} before`;
  return `${minutes} minutes before`;
}

/** Choices offered in the pickers. */
export const TIMED_ALERT_CHOICES = [0, 10, 15, 30, 60, 120, 180, 1440, 2880, 10080];
/** All-day: 6 PM / noon / 8 AM the day before, 6 PM two days before, 8 AM a week before (Google allows nothing after midnight). */
export const ALL_DAY_ALERT_CHOICES = [360, 720, 960, 1800, 9600];
